/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext } from 'react';
import { Input, InputNumber, Switch } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import TicketContext from '@totem/components/ticketing/details/ticketContext';
import BulkTimeEntryContext from '@totem/components/ticketing/details/timeEntry/bulkTimeEntry/bulkTimeEntryContext';
import { HierarchicalBulkTimeEntry } from '@totem/components/ticketing/details/timeEntry/bulkTimeEntry/types';
import { aggregateHours } from '@totem/components/ticketing/details/timeEntry/utilities';
import {
  TASK_TYPE_GROUP,
  TASK_TYPE_MILESTONE,
  TASK_TYPE_TASK,
} from '@totem/components/ticketing/ticketing';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import Hours from '@totem/components/ui_components/widgets/hours/hours';
import IBDIcon from '@totem/styles/Icon';
import { formatDateOnly, isNotNull } from '@totem/utilities/common';

const BulkTimeEntryTable = () => {
  const { ticket } = useContext(TicketContext);
  const {
    taskTimeEntryHierarchy,
    onUpdateTaskHours,
    onUpdateTaskBillable,
    onUpdateTaskDescription,
    getTimeEntry,
    timeEntryCommon,
  } = useContext(BulkTimeEntryContext);

  const getResourceById = (resourceId: string) => {
    if (
      isNotNull(ticket) &&
      isNotNull(ticket.ticket) &&
      isNotNull(ticket.ticket.partiesInvolved)
    ) {
      for (const partyInvolved of ticket.ticket.partiesInvolved) {
        if (partyInvolved.id === resourceId) {
          return partyInvolved.name;
        }
      }
    }
    return '';
  };

  const columns: ColumnProps<HierarchicalBulkTimeEntry>[] = [
    {
      title: 'ID',
      dataIndex: 'number',
      key: 'number',
      defaultSortOrder: 'ascend',
      render: (_, record: HierarchicalBulkTimeEntry) => (
        <span>
          <IBDIcon name={record.type} style={{ marginRight: '16px' }} />
          {record.number}
        </span>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (_, record: HierarchicalBulkTimeEntry) => (
        <span>{record.name}</span>
      ),
    },
    {
      title: 'Resource',
      dataIndex: 'resource',
      key: 'resource',
      render: (_, record: HierarchicalBulkTimeEntry) => (
        <b>{getResourceById(timeEntryCommon.resourceId)}</b>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (_, record: HierarchicalBulkTimeEntry) => (
        <b>{formatDateOnly(timeEntryCommon.date)}</b>
      ),
    },
    {
      title: 'Hours Worked',
      render: (_, record: HierarchicalBulkTimeEntry) => {
        if (record.type !== TASK_TYPE_TASK) {
          return <span />;
        }

        const recordTimeEntry = getTimeEntry(record.id);

        return (
          <InputNumber
            min={0}
            max={24}
            step={0.25}
            style={{ width: '120px' }}
            value={recordTimeEntry.hoursLogged}
            onChange={(value) => onUpdateTaskHours(record.id, value)}
          />
        );
      },
    },
    {
      title: 'Billable',
      render: (_, record: HierarchicalBulkTimeEntry) => {
        if (record.type !== TASK_TYPE_TASK) {
          return <span />;
        }

        const recordTimeEntry = getTimeEntry(record.id);

        return (
          <Switch
            value={recordTimeEntry.billable}
            onChange={(checked: boolean) =>
              onUpdateTaskBillable(record.id, checked)
            }
          />
        );
      },
    },
    {
      title: 'Description',
      render: (_, record: HierarchicalBulkTimeEntry) => {
        if (record.type !== TASK_TYPE_TASK) {
          return <span />;
        }

        const recordTimeEntry = getTimeEntry(record.id);

        return (
          <Input.TextArea
            rows={1}
            value={recordTimeEntry.description}
            onChange={(event) =>
              onUpdateTaskDescription(record.id, event.target.value)
            }
          />
        );
      },
    },
    {
      title: 'Budgeted Hours',
      align: 'right',
      dataIndex: 'budgetedHours',
      key: 'budgetedHours',
      render: (_, record: HierarchicalBulkTimeEntry) => {
        switch (record.type) {
          case TASK_TYPE_TASK:
            return <Hours amount={record.budgetedHours} />;
          case TASK_TYPE_GROUP:
            return (
              <b>
                <Hours amount={aggregateHours(record).budgetedHours} />
              </b>
            );
          default:
            return <span />;
        }
      },
    },
    {
      title: 'Actual Hours',
      align: 'right',
      dataIndex: 'actualHours',
      key: 'actualHours',
      render: (_, record: HierarchicalBulkTimeEntry) => {
        switch (record.type) {
          case TASK_TYPE_MILESTONE:
            return <span />;
          case TASK_TYPE_GROUP:
            return (
              <b>
                <Hours amount={aggregateHours(record).actualHours} />
              </b>
            );
          default:
            break;
        }

        const recordTimeEntry = getTimeEntry(record.id);

        if (recordTimeEntry.hoursLogged > 0) {
          return (
            <span>
              <Hours amount={record.actualHours} />{' '}
              <span style={{ color: '#ff0000' }}>
                + <Hours amount={recordTimeEntry.hoursLogged} /> ={' '}
                <Hours
                  amount={record.actualHours + recordTimeEntry.hoursLogged}
                />
              </span>
            </span>
          );
        }

        return <Hours amount={record.actualHours} />;
      },
    },
  ];

  return (
    <>
      <IBTable<HierarchicalBulkTimeEntry>
        showSorterTooltip
        columns={columns}
        pagination={{
          pageSize: 100,
        }}
        dataSource={
          isNotNull(taskTimeEntryHierarchy) ? taskTimeEntryHierarchy : []
        }
        loading={false}
        rowKey="id"
        expandable={{
          defaultExpandAllRows: true,
          expandIcon: ({ expanded, onExpand, record }) => {
            // Check if the current row actually has children
            if (isNotNull(record.children) && record.children.length > 0) {
              // Render the default Antd expand icon (or a custom one)
              return (
                <span
                  onClick={(evt) => onExpand(record, evt)}
                  style={{ cursor: 'pointer', marginRight: 8 }}
                >
                  {/* Ant Design uses a specific icon for tree expansion (usually the arrow) */}
                  {expanded ? '▼' : '▶'}
                </span>
              );
            }
            // If the row has no children, return null to hide the icon
            return <span style={{ marginRight: 8 }}>&nbsp;</span>;
          },
        }}
      />
    </>
  );
};

export default BulkTimeEntryTable;
